import React, { Component } from 'react';
import './App.css';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { SIDEBAR_DESKTOP_OFFSET, SIDEBAR_TABLET_OFFSET } from './elements';
import { initialApplicationLoad } from './actions/application';
import { Desktop, Mobile, DesktopAndTablet } from './helpers/responsive';
import { isLocal, isProd, isSandbox } from './helpers/environment';
import QuoteHelp from './Pages/QuoteHelp';
import ApplicationProgress from './Components/Global/ApplicationProgress';
import InfoSidebar from './Components/Shared/InfoSidebar';
import PolicySelection from './Pages/PolicySelection';
import Industry from './Pages/Industry';
import BusinessInfo from './Pages/BusinessInfo';
import FinanceBasics from './Pages/FinanceBasics';
import GeneralLiability from './Pages/GeneralLiability';
import Locations from './Pages/Locations';
import Summary from './Pages/Summary';
import ProfessionalLiability from './Pages/ProfessionalLiability';
import AdditionalInsureds from './Pages/AdditionalInsureds';
import Payment from './Pages/Payment';
import Contact from './Pages/Contact';
import Error from './Pages/Error';
import LoadingApplication from './Pages/LoadingApplication';
import PrefilledSummaryModal from './Components/Shared/PrefilledSummary';
import ErrorModal from './Components/Shared/ErrorModal';
import { PAGES } from './constants';
import PageHeader from './Components/Shared/PageHeader';
import AddOns from './Pages/AddOns';
import LogRocket from 'logrocket';
import { persistor } from './configureStore';
import { selectPartnerInfo } from './selectors';
import { selectAgentAgencyName, selectAgentProducerName, selectIsAgentMDA } from './selectors/agent';
import { selectUserHasExistingApplication } from './selectors/application';
import Footer from './Components/Global/Footer';
import MobileHeader from './Components/Shared/MobileHeader';

interface AppProps {
  agencyName?: string;
  producerName?: string;
  channelPartnerName?: string;
  isMDA?: boolean;
  existingApplication?: boolean;
  location: LocationState;
  initialApplicationLoad: () => void;
}

interface Pages {
  [key: string]: React.ReactNode;
}

export class App extends Component<AppProps> {
  constructor(props: AppProps) {
    super(props);
  }

  leftSidebar = () => {
    switch (this.props.location.type) {
      case PAGES.QUOTEHELP:
        return null;
      case PAGES.POLICIES:
      case PAGES.INDUSTRY:
      case PAGES.BUSINESS_INFORMATION:
      case PAGES.FINANCE_BASICS:
      case PAGES.GENERAL_LIABILITY:
      case PAGES.PROFESSIONAL_LIABILITY:
      case PAGES.LOCATIONS:
      case PAGES.CONTACT_INFORMATION:
      case PAGES.ADD_ONS:
      case PAGES.SUMMARY:
      case PAGES.PAYMENT:
      case PAGES.ADDITIONAL_INSUREDS:
        return (
          <SidebarCont data-testid="application_progress_sidebar">
            <ApplicationProgress shouldShowQuote={false} />
          </SidebarCont>
        );
      default:
        return null;
    }
  };

  newQuote = (e: KeyboardEvent) => {
    // shift + ctrl + r
    if (e.shiftKey && e.ctrlKey && (e.key === 'R' || e.key === 'r')) {
      const response = window.confirm('Are you should you want to start a new quote?');
      if (response) {
        // https://github.com/rt2zz/redux-persist?tab=readme-ov-file#persistor-object
        persistor.purge().then(() => {
          window.location.reload();
        });
      }
    }
  };

  componentDidMount() {
    // Log latest short SHA of latest commit
    console.log(import.meta.env.VITE_APP_GIT_SHA);

    const isNonInboundURL = this.props.location.type !== 'MDA_INBOUND' && this.props.location.type !== 'APPLICATION';

    if (this.props.location.type !== 'APPLICATION' && this.props.location.type !== 'ERROR') {
      this.props.initialApplicationLoad();
    }
    // track users who land directly in quote flow outside of a MDA or bridged path
    if (isNonInboundURL && !this.props.isMDA && !this.props.existingApplication) {
      LogRocket.track('direct_entry', {
        entryType: this.props.location.type,
        path: window.location.pathname,
      });
    }

    window.addEventListener('keydown', this.newQuote);

    // pendo init - https://support.pendo.io/hc/en-us/articles/360046272771-Developer-s-Guide-To-Installing-Pendo
    if (!isLocal()) {
      window.pendo.initialize({
        visitor: {
          agencyName: this.props?.agencyName,
          producerName: this.props?.producerName,
          channelPartnerName: this.props?.channelPartnerName,
        },
      });
    }

    // log rocket init - logrocket.com
    if (isProd()) {
      LogRocket.init('jkmawz/quote-flow');
    }
    if (isSandbox()) {
      LogRocket.init('jkmawz/sandbox-quoteflow');
    }
  }

  componentDidUpdate(prevProps: AppProps) {
    if (prevProps.location.type !== this.props.location.type) {
      window.scrollTo(0, 0);
      document.getElementById('mobile-main-content')?.scrollTo(0, 0);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.newQuote);
  }
  pages: Pages = {
    INDUSTRY: <Industry key={PAGES.INDUSTRY} />,
    BUSINESS_INFORMATION: <BusinessInfo key={PAGES.BUSINESS_INFORMATION} />,
    POLICIES: <PolicySelection key={PAGES.POLICIES} />,
    FINANCE_BASICS: <FinanceBasics key={PAGES.FINANCE_BASICS} />,
    PROFESSIONAL_LIABILITY: <ProfessionalLiability key={PAGES.PROFESSIONAL_LIABILITY} />,
    GENERAL_LIABILITY: <GeneralLiability key={PAGES.GENERAL_LIABILITY} />,
    LOCATIONS: <Locations key={PAGES.LOCATIONS} />,
    ADDITIONAL_INSUREDS: <AdditionalInsureds key={PAGES.ADDITIONAL_INSUREDS} />,
    CONTACT_INFORMATION: <Contact key={PAGES.CONTACT_INFORMATION} />,
    ADD_ONS: <AddOns key={PAGES.ADD_ONS} />,
    SUMMARY: <Summary key={PAGES.SUMMARY} />,
    PAYMENT: <Payment key={PAGES.PAYMENT} />,
    QUOTEHELP: <QuoteHelp key={PAGES.QUOTEHELP} />,
    APPLICATION: <LoadingApplication key={PAGES.APPLICATION} />,
    ERROR: <Error key={PAGES.ERROR} />,
  };

  rightSidebar = () => {
    switch (this.props.location.type) {
      case PAGES.POLICIES:
      case PAGES.INDUSTRY:
      case PAGES.BUSINESS_INFORMATION:
      case PAGES.FINANCE_BASICS:
      case PAGES.GENERAL_LIABILITY:
      case PAGES.PROFESSIONAL_LIABILITY:
      case PAGES.LOCATIONS:
      case PAGES.CONTACT_INFORMATION:
      case PAGES.ADD_ONS:
      case PAGES.SUMMARY:
      case PAGES.PAYMENT:
      case PAGES.ADDITIONAL_INSUREDS:
        return (
          <PriceCont data-testid="info_sidebar">
            <InfoSidebar />
          </PriceCont>
        );
      default:
        return null;
    }
  };

  render() {
    return (
      <AppContainer data-testid="main-app">
        <PrefilledSummaryModal />
        <ErrorModal />
        <ContentContainer>
          <DesktopAndTablet>
            {this.leftSidebar()}
            <PageContainer>
              <PageHeader />
              {[this.pages[this.props.location.type]]}
            </PageContainer>
            <Desktop>{this.rightSidebar()}</Desktop>
          </DesktopAndTablet>
          <Mobile>
            <MobileHeader />
            <MobileMainContent id="mobile-main-content">
              <PageHeader />
              {[this.pages[this.props.location.type]]}
            </MobileMainContent>
          </Mobile>
        </ContentContainer>
        <ChatFooter id="zendesk" />
        {this.props.location.type !== PAGES.ERROR && this.props.location.type !== PAGES.QUOTEHELP && <Footer />}
      </AppContainer>
    );
  }
}

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  a {
    color: ${({ theme }) => theme.pageComponents.globals.linkColor};
  }

  input,
  button,
  a {
    :focus {
      outline: ${({ theme }) => theme.pageComponents.globals.outlineOnFocus};
    }
  }

  @media (max-width: 767px) {
    min-height: auto;
    height: 100dvh;
    overflow: hidden;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 4;
  @media (max-width: 767px) {
    justify-content: flex-start;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }
`;

const SidebarCont = styled.div`
  margin-right: ${SIDEBAR_DESKTOP_OFFSET};
  @media (max-width: 767px) {
    display: none;
  }
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-right: ${SIDEBAR_TABLET_OFFSET};
  }
`;

const PriceCont = styled.div`
  flex: 2;
  z-index: 6;
  @media (max-width: 767px) {
    flex: 0;
  }
  @media (max-width: 1320px) {
    margin-left: 16px;
  }
`;

const MobileMainContent = styled.div`
  padding-top: 40px;
  @media (max-width: 767px) {
    padding-top: 0;
    flex: 1;
    overflow-y: auto;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  @media (max-width: 1023px) {
    overflow-x: hidden;
  }
`;

const ChatFooter = styled.div``;

const mapStateToProps = (state: ReduxState) => ({
  channelPartnerName: selectPartnerInfo(state)?.name,
  agencyName: selectAgentAgencyName(state),
  producerName: selectAgentProducerName(state),
  isMDA: selectIsAgentMDA(state),
  existingApplication: selectUserHasExistingApplication(state),
  location: state.location,
});

const mapDispatchToProps = {
  initialApplicationLoad,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
